import * as Sentry from '@sentry/vue';

export const SENTRY_DENY_URLS = Object.freeze([
  /^chrome:\/\//i,
  /chrome-extension:/i,
  /extensions\//i,
  /file:\/\//i,
  /safari-web-extension:/i,
  /safari-extension:/i,
]);

export const SENTRY_IGNORE_ERRORS = Object.freeze([
  'ResizeObserver loop completed with undelivered notifications',
]);

const compact = values => values.filter(value => Boolean(value));

export const parseSentrySampleRate = value => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0.1;
  if (parsed < 0) return 0;
  if (parsed > 1) return 1;

  return parsed;
};

export const tracePropagationTargets = ({
  origin = '',
  configuredTargets = '',
} = {}) => {
  const targets = Array.isArray(configuredTargets)
    ? configuredTargets
    : String(configuredTargets || '')
        .split(',')
        .map(target => target.trim());

  return compact([origin, /^\/api\//, /^\/rails\//, ...targets]);
};

export const buildSentryOptions = ({
  app,
  router,
  dsn,
  config = {},
  origin = '',
  sentry = Sentry,
} = {}) => {
  const targets = tracePropagationTargets({
    origin,
    configuredTargets: config.tracePropagationTargets,
  });
  const tracingIntegration = sentry.browserTracingIntegration?.({
    router,
    tracePropagationTargets: targets,
  });

  return {
    app,
    dsn,
    environment: config.environment,
    release: config.release,
    denyUrls: SENTRY_DENY_URLS,
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    tracesSampleRate: parseSentrySampleRate(config.tracesSampleRate),
    tracePropagationTargets: targets,
    integrations: compact([tracingIntegration]),
  };
};

export const setSentryRouteContext = (route, sentry = Sentry) => {
  if (!route) return;

  sentry.setTag?.('route_name', route.name || 'unknown');
  sentry.setContext?.('route', {
    name: route.name || null,
    path: route.path || null,
    fullPath: route.fullPath || null,
  });
};

const accountForUser = user => {
  const accounts = Array.isArray(user?.accounts) ? user.accounts : [];
  return accounts.find(account => account.id === user.account_id) || null;
};

export const setSentryUserContext = (user, sentry = Sentry) => {
  if (!user?.id) {
    sentry.setUser?.(null);
    return;
  }

  const account = accountForUser(user);
  const accountId = account?.id || user.account_id;
  sentry.setUser?.({ id: String(user.id) });
  sentry.setTag?.('account_id', accountId ? String(accountId) : 'unknown');
  sentry.setTag?.('user_role', user.role || 'unknown');
  sentry.setContext?.('account', {
    id: accountId || null,
    role: user.role || null,
    plan: account?.plan_name || account?.plan || null,
  });
};

const installRouteContext = (router, sentry) => {
  router?.afterEach?.(route => setSentryRouteContext(route, sentry));
};

const installVueErrorHandler = (app, sentry) => {
  if (!app?.config) return;

  const previousHandler = app.config.errorHandler;
  app.config.errorHandler = (error, instance, info) => {
    sentry.captureException?.(error, {
      extra: { vueInfo: info },
    });
    previousHandler?.(error, instance, info);
  };
};

export const configureSentry = ({
  app,
  router,
  dsn,
  config = {},
  origin = typeof window === 'undefined' ? '' : window.location.origin,
  sentry = Sentry,
} = {}) => {
  if (!dsn) return false;

  sentry.init(buildSentryOptions({ app, router, dsn, config, origin, sentry }));
  installRouteContext(router, sentry);
  installVueErrorHandler(app, sentry);
  return true;
};
